/* Ash Wishlist — localStorage-backed favourites with cross-tab sync.
 * Stores product handles. Header bubble + drawer count update automatically.
 * ---------------------------------------------------------------------
 * Public API (window.AshWishlist):
 *   read(), has(handle), toggle(handle), remove(handle), clear()
 *   open(), close()      — control the ash-wishlist-drawer
 *
 * DOM contract:
 *   [data-ash-wl="<handle>"]       — toggle button (heart icon)
 *   [data-ash-wl-count]            — count bubble (auto-hidden when 0)
 *   [data-ash-wl-open]             — trigger that opens the drawer
 *   [data-ash-wl-close]            — element inside drawer that closes it
 *   [data-ash-wl-drawer]           — drawer wrapper (single instance)
 *   [data-ash-wl-list]             — <ul> where items are hydrated
 *   [data-ash-wl-empty]            — empty-state block (auto-toggled)
 *   [data-ash-wishlist-page]       — legacy full-page section (kept)
 */
(function () {
  if (window.AshWishlist) return;

  var KEY = 'ash-wishlist-v1';

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function write(arr) {
    try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch (e) {}
    notify();
  }
  function has(handle) { return read().indexOf(handle) !== -1; }
  function toggle(handle) {
    var arr = read();
    var i = arr.indexOf(handle);
    if (i === -1) arr.unshift(handle);
    else arr.splice(i, 1);
    write(arr);
    return arr.indexOf(handle) !== -1;
  }
  function remove(handle) {
    var arr = read();
    var i = arr.indexOf(handle);
    if (i !== -1) arr.splice(i, 1);
    write(arr);
  }
  function clear() { write([]); }

  function syncButton(btn) {
    var handle = btn.getAttribute('data-ash-wl');
    if (!handle) return;
    var active = has(handle);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('is-active', active);
  }
  function syncAllButtons() {
    document.querySelectorAll('[data-ash-wl]').forEach(syncButton);
  }
  function syncBubbles() {
    var count = read().length;
    /* Pill / floating count bubble — auto-hides at 0. */
    document.querySelectorAll('[data-ash-wl-count]').forEach(function (el) {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
    /* In-heading count number — always visible. */
    document.querySelectorAll('[data-ash-wl-count-text]').forEach(function (el) {
      el.textContent = count;
    });
    /* Pluralisation ("Item" vs "Items"). Hide the trailing "s" at 1. */
    document.querySelectorAll('[data-ash-wl-plural-s]').forEach(function (el) {
      el.toggleAttribute('hidden', count === 1);
    });
  }
  function notify() {
    syncAllButtons();
    syncBubbles();
    document.dispatchEvent(new CustomEvent('ash-wishlist:change', { detail: { items: read() } }));
  }

  /* -------- click delegation: TOGGLE a saved handle ------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-ash-wl]');
    if (!btn) return;
    e.preventDefault();
    toggle(btn.getAttribute('data-ash-wl'));
  });

  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { syncAllButtons(); syncBubbles(); }
  });

  /* -------- drawer: open / close / hydrate --------------------------- */
  function drawer() { return document.querySelector('[data-ash-wl-drawer]'); }
  function openDrawer() {
    var d = drawer();
    if (!d) return;
    d.removeAttribute('hidden');
    /* two-frame delay so the browser applies the initial "closed"
       transform before we flip to open — otherwise the slide-in
       animation snaps on first open. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        d.setAttribute('data-open', 'true');
        d.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('ash-wl-locked');
      });
    });
    hydrateDrawer();
  }
  function closeDrawer() {
    var d = drawer();
    if (!d) return;
    d.removeAttribute('data-open');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('ash-wl-locked');
    /* Wait for the slide-out transition to finish before setting
       hidden — otherwise the panel disappears instantly. */
    setTimeout(function () {
      if (d.getAttribute('data-open') !== 'true') d.setAttribute('hidden', '');
    }, 280);
  }

  document.addEventListener('click', function (e) {
    var opener = e.target && e.target.closest && e.target.closest('[data-ash-wl-open]');
    if (opener) { e.preventDefault(); openDrawer(); return; }
    var closer = e.target && e.target.closest && e.target.closest('[data-ash-wl-close]');
    if (closer) { e.preventDefault(); closeDrawer(); return; }
    var remover = e.target && e.target.closest && e.target.closest('[data-ash-wl-remove]');
    if (remover) {
      e.preventDefault();
      var h = remover.getAttribute('data-ash-wl-remove');
      if (h) remove(h);
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var d = drawer();
      if (d && d.getAttribute('data-open') === 'true') closeDrawer();
    }
  });

  /* -------- drawer hydration: fetch product data per handle ---------- */
  function drawerRow(product) {
    var li = document.createElement('li');
    li.className = 'ash-wl-drawer__item';
    li.setAttribute('data-handle', product.handle);
    var img = product.image || (product.featured_image ? product.featured_image : '');
    /* Shopify JSON returns image as a full URL string or as { src: ... }. */
    var imgUrl = typeof img === 'string' ? img : (img && img.src) || '';
    var price = (product.price != null) ? (product.price / 100) : null;
    var currency = product.currency || '';
    li.innerHTML = ''
      + (imgUrl ? '<a href="' + product.url + '"><img class="ash-wl-drawer__thumb" src="' + imgUrl + '&width=180" alt="' + (product.title || '') + '" loading="lazy" width="64" height="80"></a>' : '<span class="ash-wl-drawer__thumb"></span>')
      + '<div class="ash-wl-drawer__meta">'
      +   '<a class="ash-wl-drawer__title" href="' + product.url + '">' + (product.title || product.handle) + '</a>'
      +   (price != null ? '<p class="ash-wl-drawer__price">' + currency + ' ' + price.toFixed(2) + '</p>' : '')
      + '</div>'
      + '<button type="button" class="ash-wl-drawer__remove" data-ash-wl-remove="' + product.handle + '" aria-label="Remove ' + (product.title || product.handle) + ' from wishlist">'
      +   '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
      + '</button>';
    return li;
  }

  function hydrateDrawer() {
    var d = drawer();
    if (!d) return;
    var listEl = d.querySelector('[data-ash-wl-list]');
    var emptyEl = d.querySelector('[data-ash-wl-empty]');
    var handles = read();
    if (!handles.length) {
      if (listEl) listEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    if (!listEl) return;
    /* Diff — rebuild list preserving current order (most-recent first). */
    listEl.innerHTML = '';
    handles.forEach(function (handle) {
      var ph = document.createElement('li');
      ph.className = 'ash-wl-drawer__item ash-wl-drawer__item--loading';
      ph.setAttribute('data-handle', handle);
      ph.innerHTML = '<span class="ash-wl-drawer__thumb"></span><span></span>';
      listEl.appendChild(ph);
      fetch('/products/' + encodeURIComponent(handle) + '.json', { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('missing');
          return r.json();
        })
        .then(function (data) {
          var p = data && data.product;
          if (!p) { ph.remove(); remove(handle); return; }
          var v = (p.variants && p.variants[0]) || {};
          ph.replaceWith(drawerRow({
            handle: p.handle,
            title: p.title,
            url: '/products/' + p.handle,
            image: (p.image && p.image.src) || (p.images && p.images[0] && p.images[0].src) || '',
            price: v.price != null ? Math.round(parseFloat(v.price) * 100) : null,
            currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || ''
          }));
        })
        .catch(function () {
          ph.remove();
          remove(handle);
        });
    });
  }

  function onReady() {
    syncAllButtons();
    syncBubbles();
    hydratePageList();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  /* -------- legacy wishlist PAGE (kept for pages/wishlist route) ---- */
  function hydratePageList() {
    var page = document.querySelector('[data-ash-wishlist-page]');
    if (!page) return;
    var emptyEl = page.querySelector('[data-ash-wl-empty]');
    var listEl = page.querySelector('[data-ash-wl-list]');
    var handles = read();

    if (!handles.length) {
      if (emptyEl) emptyEl.hidden = false;
      if (listEl) listEl.hidden = true;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    if (listEl) {
      listEl.hidden = false;
      listEl.innerHTML = '';
      handles.forEach(function (handle) {
        var ph = document.createElement('div');
        ph.className = 'ash-wl-page__placeholder';
        ph.setAttribute('data-handle', handle);
        listEl.appendChild(ph);
        fetch('/products/' + encodeURIComponent(handle) + '?view=ash-wishlist-card', { headers: { 'Accept': 'text/html' } })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var div = document.createElement('div');
            div.innerHTML = html.trim();
            var card = div.querySelector('[data-ash-wl-card]') || div.firstElementChild;
            if (card) ph.replaceWith(card);
            else ph.remove();
          })
          .catch(function () {
            ph.remove();
            remove(handle);
          });
      });
    }
  }

  document.addEventListener('ash-wishlist:change', function () {
    hydratePageList();
    var d = drawer();
    if (d && d.getAttribute('data-open') === 'true') hydrateDrawer();
    else if (d) {
      /* Update empty-state visibility even when drawer is closed so
         it's already correct next time it opens. */
      var handles = read();
      var emptyEl = d.querySelector('[data-ash-wl-empty]');
      if (emptyEl) emptyEl.hidden = handles.length > 0;
    }
  });

  window.AshWishlist = {
    read: read, has: has, toggle: toggle, remove: remove, clear: clear,
    open: openDrawer, close: closeDrawer
  };
})();
